import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

export default function ModalSearchSubscription(props) {
  const modalRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        props.onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line 
  }, [props.onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const handleSignupAs = (type) => {
    props.onClose();
    navigate(`/account/signup?type=${type}`);
  };

  return ReactDOM.createPortal(
    <AnimatePresence>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6"
        onClick={props.onClose}
      >
        {/* Modal Content */}
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-[500px] relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={props.onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10"
          >
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <div className="p-6 sm:p-8 text-center">
            {/* Icon */}
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
              <span className="text-4xl">🔓</span>
            </div>

            {/* Title */}
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 leading-tight">
              Unlock Premium Features
            </h3>

            {/* Description */}
            <p className="text-sm sm:text-base text-gray-500 mb-6 leading-relaxed">
              Get full access to influencer search, analytics, and campaign
              tools. Choose your account type to get started.
            </p>

            {/* Buttons Container */}
            <div className="flex flex-col gap-3 mb-6">
              {/* Brand Button */}
              <button
                onClick={() => navigate("/account/pricing")}
                className="flex items-center justify-center gap-3 py-3.5 px-5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl text-sm sm:text-base font-semibold transition-all duration-300 shadow-lg hover:-translate-y-0.5 hover:shadow-xl active:scale-[0.98]"
              >
                <span className="text-xl">🏢</span>
                Sign up as Brand
              </button>

              {/* Influencer Button */}
              <button
                onClick={() => handleSignupAs("influencer")}
                className="flex items-center justify-center gap-3 py-3.5 px-5 bg-gradient-to-r from-pink-400 to-rose-500 text-white rounded-xl text-sm sm:text-base font-semibold transition-all duration-300 shadow-lg hover:-translate-y-0.5 hover:shadow-xl active:scale-[0.98]"
              >
                <span className="text-xl">⭐</span>
                Sign up as Influencer
              </button>
            </div>

            {/* Divider */}
            <div className="flex items-center my-4">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="px-3 text-gray-400 text-xs">or</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            {/* Sign In Link */}
            <p className="text-gray-500 text-sm">
              Already have an account?{" "}
              <Link
                to="/account/login"
                className="text-indigo-500 font-semibold hover:text-indigo-600 transition-colors"
                onClick={props.onClose}
              >
                Sign in
              </Link>
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.getElementById("modals")
  );
}
